import {
  useMutation,
  useQuery,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { ClientError, request } from 'graphql-request'
import { RedirectRequest, SilentRequest } from '@azure/msal-browser'
import { loginRequest } from '@/auth-config'
import { showErrorNotification } from '@shared/helpers/notifications/notifications'
import { getAuthTokenPromise, useGetAuthToken } from '@/hooks/use-get-auth-token'
import { GrowerServiceMutation, GrowerServiceQuery } from '@grower-api-types'
import { configService } from '@/services/config-service'
import { useMsal } from '@azure/msal-react'
import { findForbiddenError, retryChecker } from 'shared/src/utils/query-utils'
import { loggingService } from '@/services/logging-service'

type GraphProps = {
  gql: string | null
  queryParams?: object
  msalRequest?: SilentRequest | RedirectRequest
}

type GraphQueryFunction = (
  props: GraphProps &
    UseQueryOptions & {
      alwaysThrowOnError?: boolean
    }
) => UseQueryResult<GrowerServiceQuery, ClientError>

type GraphMutationFunction = (
  props: GraphProps &
    UseMutationOptions<GrowerServiceMutation, Error, Record<string, any>, unknown> & {
      gqlKey: keyof Omit<GrowerServiceMutation, '__typename'>
      onSettled?: () => Promise<void>
      onError?: () => void
    }
) => UseMutationResult<GrowerServiceMutation, ClientError, Record<string, any>, unknown>

type RestProps = {
  endpoint: string
  msalRequest?: SilentRequest | RedirectRequest
}

type QueryFunction = (
  props: RestProps &
    UseQueryOptions & {
      alwaysThrowOnError?: boolean
    }
) => UseQueryResult<object, Error>

const defaultErrorMessage =
  'An error has occured. Please contact your Zentera Area Manager or Zentera Support.'

const shouldThrowError = (error: ClientError) => {
  // Handle 403 forbidden error from GraphQL
  const forbiddenError = findForbiddenError(error)
  if (forbiddenError) {
    error.response.status = 403
    error.message = forbiddenError.message
  }

  // Handle other errors
  return (
    error.response?.status === 401 || // all Unauthorized errors will go to the Error Boundary
    error.response?.status >= 500
  ) // all other server errors will go to the Error Boundary
}

const buildUrl = (endpoint: string) => [configService().get('API_HOST_URI'), endpoint].join('/')

/**
 * Wraps a GraphQL query in a useQuery hook that requires authentication.
 * @param queryKey A unique key for the query.
 * @param gql The GQL object to define the query.
 * @param msalRequest The MSAL request object to use for authentication, defaults to Read scope.
 */
export const useAuthenticatedGraphQuery: GraphQueryFunction = ({
  queryKey,
  gql,
  queryParams,
  msalRequest = loginRequest,
  enabled = true,
  alwaysThrowOnError = false,
}) => {
  const accessToken = useGetAuthToken(msalRequest, queryKey)
  const headers = new Headers({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  })
  // Note: Adding a retry prop will ignore defaults set in the QueryClient.
  return useQuery<GrowerServiceQuery, ClientError>({
    queryKey,
    queryFn: () => request(buildUrl('api/graphql'), gql ?? '', queryParams ?? {}, headers),
    enabled: enabled && !!accessToken, // Don't send the request if the access token is not available yet
    // If alwaysThrowOnError is true, all errors will go to the Error Boundary after the retries
    // Otherwise, conditionally throw error if shouldThrowError is true
    throwOnError: (error) => {
      loggingService?.captureError(error, {
        labels: { source: 'useAuthenticatedGraphQuery' },
      })
      return alwaysThrowOnError || shouldThrowError(error)
    },
    retry: retryChecker,
  })
}

/**
 * Custom error type for GraphQL mutation errors
 */
export class GraphMutationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GraphMutationError'
  }
}

/**
 *
 * @param {Object} params - Parameters for the mutation.
 * @param {string} params.gql - The GraphQL mutation string.
 * @param {string} params.gqlKey - The key to access the mutation result in the response.
 * @param {() => Promise<void>} params.onSettled - Optional function to run as soon as mutation starts execution. Use this to invalidate cache for non-optimistic updates.
 * @param {() => void} params.onError - Optional function to run if there was an error returned by the mutation. Use this to invalidate cache for optimistic updates.
 * @param {Object} [params.msalRequest=loginRequest] - The MSAL request object to use for authentication, defaults to Read scope.
 * @param {Object} params.mutationOptions - Additional options for `useMutation()`.
 *
 * @returns {MutationResult} The result of the mutation.
 *
 * @throws {Error} If the mutation response indicates a failure.
 */
export const useAuthenticatedGraphMutation: GraphMutationFunction = ({
  gql,
  gqlKey,
  onSettled,
  onError,
  msalRequest = loginRequest,
  ...mutationOptions
}) => {
  // TODO: Remove duplication between the gql and gqlKey parameters.
  const { instance } = useMsal()
  return useMutation<GrowerServiceMutation, ClientError, Record<string, any>>({
    mutationFn: async (variables: Record<string, any>) => {
      const accessToken = await getAuthTokenPromise(instance, msalRequest)
      const headers = new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      })
      const response = await request<GrowerServiceMutation>(
        buildUrl('api/graphql'),
        gql ?? '',
        variables ?? {},
        headers
      )

      // Check if response[gqlKey] is a MutationResponse type by checking for the presence of expected properties
      const mutationResponse = response[gqlKey]
      const isMutationResponse = mutationResponse && 'success' in mutationResponse

      // Special default error handling for MutationResponse
      if (isMutationResponse && mutationResponse.success === false) {
        throw new GraphMutationError(mutationResponse.errorMessage || defaultErrorMessage)
      }
      return response
    },
    // Conditionally throw error if shouldThrowError is true (throwOnError results in React Error Boundary catching it)
    throwOnError: (error) => shouldThrowError(error),
    onError: (error) => {
      loggingService?.captureError(error, {
        labels: { source: 'useAuthenticatedGraphMutation' },
      })
      if (!shouldThrowError(error)) {
        // Show notification with server message if provided.
        if (error instanceof GraphMutationError) {
          showErrorNotification(error.message)
        } else {
          showErrorNotification(defaultErrorMessage)
        }
      }
      // If defined, execute additional onError behaviour passed from the top, i.e. invalidation of cache for optimistic updates
      if (onError) {
        onError()
      }
    },
    onSettled: async () => {
      // If defined, execute onSettled behaviour passed from the top, i.e. invalidation of cache for non-optimistic updates
      if (onSettled) {
        // The promise has to be returned from the query invalidation, so that the mutation stays in `pending` state until the refetch is finished
        return onSettled()
      }
      return null
    },
    ...mutationOptions,
  })
}

/**
 * Wraps a REST query in a useQuery hook that requires authentication.
 * @param queryKey A unique key for the query.
 * @param endpoint The endpoint to query.
 * @param msalRequest The MSAL request object to use for authentication, defaults to Read scope.
 */
export const useAuthenticatedRESTQuery: QueryFunction = ({
  queryKey,
  endpoint,
  msalRequest = loginRequest,
  alwaysThrowOnError = false,
}) => {
  const accessToken = useGetAuthToken(msalRequest, queryKey)
  const headers = new Headers({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  })
  return useQuery({
    queryKey,
    queryFn: async () => {
      const response: Response = await fetch(buildUrl(endpoint), { headers })
      const json = await response.json()
      return json
    },
    enabled: !!accessToken, // Don't send the request if the access token is not available yet
    // If alwaysThrowOnError is true, all errors will go to the Error Boundary after the retries
    // Otherwise, conditionally throw error if shouldThrowError is true
    throwOnError: (error: ClientError) => {
      loggingService?.captureError(error, {
        labels: { source: 'useAuthenticatedRESTQuery' },
      })
      return alwaysThrowOnError || shouldThrowError(error)
    },
  })
}
